import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, Minus, Plus, Trash2, ChevronRight } from "lucide-react";
import { announcements, navLinks } from "../mock";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Link as RLink } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";

const AnnouncementBar = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % announcements.length), 3500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="bg-[#050304] text-[#F3E7D8] text-center text-xs sm:text-[13px] tracking-wide py-2 px-4 font-medium border-b border-white/10">
      {announcements[idx]}
    </div>
  );
};

const CartDrawer = () => {
  const { isCartOpen, setCartOpen, items, updateQty, removeFromCart, cartTotal, cartMrpTotal } = useCart();
  const navigate = useNavigate();
  const savings = cartMrpTotal - cartTotal;

  return (
    <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 bg-white">
        <SheetHeader className="px-5 py-4 border-b border-neutral-200">
          <SheetTitle className="font-serif text-lg tracking-wide text-neutral-900">
            YOUR CART
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="h-12 w-12 text-neutral-300" />
            <p className="text-neutral-600">Your cart is currently empty</p>
            <button
              onClick={() => {
                setCartOpen(false);
                navigate("/collections/bestsellers");
              }}
              className="bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-6 py-2.5 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity"
            >
              Shop Bestsellers
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((item) => (
                <div key={item.key} className="flex gap-3 border-b border-neutral-100 pb-4">
                  <img src={item.image} alt={item.name} className="h-24 w-20 object-cover rounded-sm bg-[#f6f3ee]" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-semibold text-neutral-900 leading-tight">{item.name}</p>
                      <button onClick={() => removeFromCart(item.key)} aria-label="Remove">
                        <Trash2 className="h-4 w-4 text-neutral-400 hover:text-[#D6425F]" />
                      </button>
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5">{item.size}</p>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center border border-neutral-300 rounded-sm">
                        <button onClick={() => updateQty(item.key, item.qty - 1)} className="px-2 py-1" aria-label="Decrease">
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-sm">{item.qty}</span>
                        <button onClick={() => updateQty(item.key, item.qty + 1)} className="px-2 py-1" aria-label="Increase">
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="text-sm font-bold text-neutral-900">
                        ₹{(item.price * item.qty).toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-neutral-200 px-5 py-4 space-y-3">
              {savings > 0 && (
                <p className="text-xs text-[#D6425F] font-medium">
                  You're saving ₹{savings.toLocaleString("en-IN")} on this order 🎉
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">Subtotal</span>
                <span className="text-lg font-bold text-neutral-900">₹{cartTotal.toLocaleString("en-IN")}</span>
              </div>
              <button
                onClick={() => {
                  setCartOpen(false);
                  navigate("/cart");
                }}
                className="w-full bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] py-3 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                Checkout <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Header = () => {
  const { cartCount, setCartOpen, wishlist } = useCart();
  const { user, isAdmin, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      <AnnouncementBar />
      <div className={`bg-[#050304] transition-shadow ${scrolled ? "shadow-lg" : ""}`}>
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Left */}
            <div className="flex items-center gap-2 flex-1">
              <button className="lg:hidden text-white" onClick={() => setMobileOpen(true)} aria-label="Menu">
                <Menu className="h-6 w-6" />
              </button>
              <button className="hidden lg:flex text-white/80 hover:text-[#FFE29A] transition-colors" aria-label="Search">
                <Search className="h-5 w-5" />
              </button>
            </div>

            {/* Center: logo */}
            <Link to="/" className="flex items-center justify-center">
              <img src="/logo.png?v=deegford-20261011" alt="Deegford" className="h-16 lg:h-20 w-auto object-contain" />
            </Link>

            {/* Right: icons */}
            <div className="flex items-center gap-4 lg:gap-5 flex-1 justify-end text-white">
              <div className="hidden sm:block relative group">
                <button className="hover:text-[#FFE29A] transition-colors flex items-center" aria-label="Account">
                  <User className="h-5 w-5" />
                </button>
                <div className="invisible group-hover:visible absolute right-0 top-full bg-white shadow-xl rounded-sm py-2 min-w-[180px] opacity-0 group-hover:opacity-100 transition-opacity z-50">
                  {user ? (
                    <>
                      <p className="px-4 py-2 text-xs text-neutral-500 border-b border-neutral-100">Hi, {user.name?.split(" ")[0]}</p>
                      <RLink to="/profile" className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F]">My Profile</RLink>
                      {isAdmin && (
                        <RLink to="/admin" className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F]">Admin Panel</RLink>
                      )}
                      <RLink to="/wishlist" className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F]">My Wishlist</RLink>
                      <button onClick={() => { logout(); navigate("/"); }} className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F]">Logout</button>
                    </>
                  ) : (
                    <>
                      <RLink to="/login" className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F]">Sign In</RLink>
                      <RLink to="/register" className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F]">Create Account</RLink>
                    </>
                  )}
                </div>
              </div>
              <Link to="/wishlist" className="relative hover:text-[#FFE29A] transition-colors" aria-label="Wishlist">
                <Heart className="h-5 w-5" />
                {wishlist.length > 0 && (
                  <span className="absolute -right-2 -top-2 bg-gradient-to-br from-[#FF8FA3] to-[#D6425F] text-white text-[10px] font-bold h-4 w-4 rounded-full flex items-center justify-center">
                    {wishlist.length}
                  </span>
                )}
              </Link>
              <button onClick={() => setCartOpen(true)} className="relative hover:text-[#FFE29A] transition-colors" aria-label="Cart">
                <ShoppingBag className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -right-2 -top-2 bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] text-[10px] font-bold h-4 w-4 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:block border-t border-white/10">
          <ul className="mx-auto max-w-[1400px] px-8 flex items-center justify-center gap-9 h-12">
            {navLinks.map((link) => (
              <li key={link.label} className="group relative h-full flex items-center">
                <Link
                  to={link.to}
                  className="text-[13px] font-medium tracking-widest text-white/90 hover:text-[#FFE29A] transition-colors"
                >
                  {link.label}
                </Link>
                {link.children && (
                  <div className="invisible group-hover:visible absolute left-1/2 top-full -translate-x-1/2 bg-white shadow-xl rounded-sm py-2 min-w-[180px] opacity-0 group-hover:opacity-100 transition-opacity z-50">
                    {link.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        className="block px-5 py-2.5 text-sm text-neutral-700 hover:bg-[#f6f3ee] hover:text-[#C8932F] transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Mobile menu */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[300px] bg-[#050304] p-0 border-r-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <img src="/logo.png" alt="Deegford" className="h-10 w-auto object-contain" />
            <button onClick={() => setMobileOpen(false)} className="text-white"><X className="h-5 w-5" /></button>
          </div>
          {user && (
            <div className="px-5 py-3 border-b border-white/10">
              <p className="text-xs text-white/50 uppercase tracking-widest">Hi, {user.name?.split(" ")[0]}</p>
              <RLink to="/profile" onClick={() => setMobileOpen(false)} className="text-sm text-[#FFE29A] hover:underline">My Profile & Orders</RLink>
            </div>
          )}
          <ul className="py-2">
            {navLinks.map((link) => (
              <li key={link.label} className="border-b border-white/5">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate(link.to);
                  }}
                  className="w-full flex items-center justify-between px-5 py-4 text-white text-sm tracking-widest hover:text-[#FFE29A] transition-colors"
                >
                  {link.label}
                  <ChevronRight className="h-4 w-4 opacity-50" />
                </button>
              </li>
            ))}
          </ul>
          {!user && (
            <div className="px-5 py-4 border-t border-white/10 flex flex-col gap-2">
              <RLink to="/login" onClick={() => setMobileOpen(false)} className="w-full text-center bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] py-2.5 text-sm font-semibold uppercase tracking-widest rounded-sm">Sign In</RLink>
              <RLink to="/register" onClick={() => setMobileOpen(false)} className="w-full text-center border border-white/20 text-white py-2.5 text-sm uppercase tracking-widest rounded-sm">Create Account</RLink>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <CartDrawer />
    </header>
  );
};

export default Header;
